import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { Users, Filter, Download, Trash2 } from 'lucide-react';

interface WaitlistEntry {
  _id: string;
  name: string;
  email: string;
  company?: string;
  role?: string;
  recruiterType?: string;
  fieldOfWork?: string;
  message?: string;
  courseType: 'free' | 'advanced' | 'premium';
  courseName: string;
  createdAt: string;
}

interface AdminWaitlistProps {
  user: any;
  onLogout: () => void;
}

export function AdminWaitlist({ user, onLogout }: AdminWaitlistProps) {
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'free' | 'advanced' | 'premium'>('all');
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);

  useEffect(() => {
    fetchWaitlistEntries();
  }, []);

  const fetchWaitlistEntries = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl(`/api/waitlist?_t=${Date.now()}`), {
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const data = await response.json();

      if (data.success) {
        setWaitlistEntries(data.data);
      } else {
        setError('Failed to fetch waitlist entries');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this waitlist entry?')) {
      return;
    }

    try {
      console.log('🗑️ Attempting to delete waitlist entry:', id);
      const response = await fetch(apiUrl(`/api/waitlist/${id}`), {
        method: 'DELETE'
      });

      const data = await response.json();
      console.log('📋 Delete response:', data);

      if (response.ok && data.success) {
        console.log('✅ Delete successful, updating UI');
        setWaitlistEntries(prev => prev.filter(entry => entry._id !== id));
        setError(''); // Clear any previous errors
      } else {
        console.error('❌ Delete failed:', data.message);
        setError(`Failed to delete entry: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('❌ Network error during delete:', err);
      setError('Network error during delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedEntries.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedEntries.length} entries?`)) {
      return;
    }

    try {
      console.log('🗑️ Attempting bulk delete of entries:', selectedEntries);
      const deletePromises = selectedEntries.map(async (id) => {
        const response = await fetch(apiUrl(`/api/waitlist/${id}`), { method: 'DELETE' });
        const data = await response.json();
        return { id, success: response.ok && data.success, data };
      });
      
      const results = await Promise.all(deletePromises);
      const successfulDeletes = results.filter(r => r.success).map(r => r.id);
      const failedDeletes = results.filter(r => !r.success);
      
      console.log('📋 Bulk delete results:', { successful: successfulDeletes, failed: failedDeletes });
      
      if (successfulDeletes.length > 0) {
        setWaitlistEntries(prev => prev.filter(entry => !successfulDeletes.includes(entry._id)));
        setSelectedEntries([]);
      }
      
      if (failedDeletes.length > 0) {
        setError(`Failed to delete ${failedDeletes.length} entries. Check console for details.`);
      } else {
        setError(''); // Clear errors if all successful
      }
    } catch (err) {
      console.error('❌ Bulk delete error:', err);
      setError('Failed to delete entries');
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Name', 'Email', 'Company', 'Role', 'Recruiter Type', 'Field of Work', 'Course Type', 'Course Name', 'Message', 'Date'],
      ...waitlistEntries
        .filter(entry => filter === 'all' || entry.courseType === filter)
        .map(entry => [
          entry.name,
          entry.email,
          entry.company || '',
          entry.role || '',
          entry.recruiterType || '',
          entry.fieldOfWork || '',
          entry.courseType,
          entry.courseName,
          entry.message || '',
          new Date(entry.createdAt).toLocaleDateString()
        ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredEntries = waitlistEntries.filter(entry => 
    filter === 'all' || entry.courseType === filter
  );

  const handleSelectEntry = (id: string) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedEntries.length === filteredEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(filteredEntries.map(entry => entry._id));
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '1.1rem',
        color: 'var(--gray)'
      }}>
        Loading waitlist entries...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ 
          fontSize: '2rem', 
          fontWeight: '700', 
          color: 'var(--dark)', 
          margin: '0 0 0.5rem 0' 
        }}>
          Waitlist Management
        </h1>
        <p style={{ 
          color: 'var(--gray)', 
          fontSize: '1.1rem', 
          margin: '0' 
        }}>
          Manage course waitlist signups and track interest
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          border: '1px solid #eee',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--orange)', marginBottom: '0.5rem' }}>
            {waitlistEntries.length}
          </div>
          <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Total Signups</div>
        </div>
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          border: '1px solid #eee',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b', marginBottom: '0.5rem' }}>
            {waitlistEntries.filter(e => e.courseType === 'free').length}
          </div>
          <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Free Course Requests</div>
        </div>
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          border: '1px solid #eee',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10b981', marginBottom: '0.5rem' }}>
            {waitlistEntries.filter(e => e.courseType === 'advanced').length}
          </div>
          <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Advanced Course</div>
        </div>
        <div style={{ 
          background: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          border: '1px solid #eee',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: '#8b5cf6', marginBottom: '0.5rem' }}>
            {waitlistEntries.filter(e => e.courseType === 'premium').length}
          </div>
          <div style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>Premium Course</div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ 
        background: 'white', 
        padding: '1.5rem', 
        borderRadius: '12px', 
        border: '1px solid #eee',
        marginBottom: '2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Filter size={16} color="var(--gray)" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}
            >
              <option value="all">All Courses</option>
              <option value="free">Free Course (Invitation Requests)</option>
              <option value="advanced">Advanced Course</option>
              <option value="premium">Premium Course</option>
            </select>
          </div>
          
          {selectedEntries.length > 0 && (
            <button
              onClick={handleBulkDelete}
              style={{
                background: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              <Trash2 size={16} />
              Delete Selected ({selectedEntries.length})
            </button>
          )}
        </div>

        <button
          onClick={exportToCSV}
          style={{
            background: 'var(--orange)',
            color: 'var(--dark)',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      {/* Waitlist Entries */}
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        border: '1px solid #eee',
        overflow: 'hidden'
      }}>
        {error && (
          <div style={{ 
            background: '#fee2e2', 
            color: '#991b1b', 
            padding: '1rem', 
            borderBottom: '1px solid #fecaca' 
          }}>
            {error}
          </div>
        )}

        {filteredEntries.length === 0 ? (
          <div style={{ 
            padding: '3rem', 
            textAlign: 'center', 
            color: 'var(--gray)' 
          }}>
            <Users size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No waitlist entries found</div>
            <div style={{ fontSize: '0.9rem' }}>
              {filter === 'all' ? 'No one has signed up yet.' : `No signups for ${filter} course.`}
            </div>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '40px 1.2fr 1.2fr 1fr 1.2fr 1fr 80px', 
              gap: '1rem', 
              padding: '1rem', 
              background: '#f8f9fa', 
              borderBottom: '1px solid #eee',
              fontWeight: '600',
              fontSize: '0.9rem',
              color: 'var(--dark)'
            }}>
              <div>
                <input
                  type="checkbox"
                  checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0}
                  onChange={handleSelectAll}
                  style={{ transform: 'scale(1.2)' }}
                />
              </div>
              <div>Name & Profile</div>
              <div>Email</div>
              <div>Company</div>
              <div>Course</div>
              <div>Date</div>
              <div>Actions</div>
            </div>

            {/* Table Rows */}
            {filteredEntries.map((entry) => {
              const recruiterType = (entry.recruiterType || '').toLowerCase();
              let recruiterBadgeStyles = { background: '#ede9fe', color: '#5b21b6' };
              let recruiterLabel = 'Recruiter';

              if (recruiterType === 'internal') {
                recruiterBadgeStyles = { background: '#e0f2fe', color: '#0369a1' };
                recruiterLabel = 'Internal Recruiter';
              } else if (recruiterType === 'external') {
                recruiterBadgeStyles = { background: '#fef3c7', color: '#b45309' };
                recruiterLabel = 'External Recruiter';
              } else if (recruiterType === 'hybrid') {
                recruiterBadgeStyles = { background: '#ede9fe', color: '#5b21b6' };
                recruiterLabel = 'Hybrid Recruiter';
              } else if (recruiterType) {
                recruiterLabel = entry.recruiterType || 'Recruiter';
              }

              return (
                <div
                  key={entry._id}
                  style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '40px 1.2fr 1.2fr 1fr 1.2fr 1fr 80px', 
                    gap: '1rem', 
                    padding: '1rem', 
                    borderBottom: '1px solid #f0f0f0',
                    alignItems: 'center',
                    fontSize: '0.9rem'
                  }}
                >
                  <div>
                    <input
                      type="checkbox"
                      checked={selectedEntries.includes(entry._id)}
                      onChange={() => handleSelectEntry(entry._id)}
                      style={{ transform: 'scale(1.2)' }}
                    />
                  </div>
                  <div style={{ color: 'var(--dark)' }}>
                    <div style={{ fontWeight: '600' }}>{entry.name}</div>
                    {entry.role && (
                      <div style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        {entry.role}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                      {recruiterType && (
                        <span style={{
                          ...recruiterBadgeStyles,
                          padding: '0.2rem 0.5rem',
                          borderRadius: '999px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: '0.03em'
                        }}>
                          {recruiterLabel}
                        </span>
                      )}
                      {entry.fieldOfWork && (
                        <span style={{
                          background: '#fefce8',
                          color: '#92400e',
                          padding: '0.2rem 0.6rem',
                          borderRadius: '999px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          letterSpacing: '0.03em'
                        }}>
                          {entry.fieldOfWork}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ color: 'var(--gray)' }}>
                    {entry.email}
                  </div>
                  <div style={{ color: 'var(--gray)' }}>
                    {entry.company || '-'}
                  </div>
                  <div>
                    <span style={{
                      background: entry.courseType === 'free' ? '#f59e0b' : 
                                 entry.courseType === 'advanced' ? '#10b981' : '#8b5cf6',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      fontWeight: '600'
                    }}>
                      {entry.courseType === 'free' ? 'INVITATION REQUEST' : entry.courseType.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>
                    {new Date(entry.createdAt).toLocaleDateString()}
                  </div>
                  <div>
                    <button
                      onClick={() => handleDeleteEntry(entry._id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Delete entry"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
